import {TABLE_AXIS_CELL_HEIGHT} from 'notebook/tables/constants/TableLayoutConstants';
import {
  computeMaxTableHeight,
  computeMinTableHeight,
} from 'notebook/tables/store/logic/TableMeasurementsLogic';
import {createTableModelSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

import {TableViewsModel} from './TableViewsSelectors';

/**
 * Model containing prop/state-derived table measurements. DOM-dependent
 * measurements are not part of this model and remain in the component.
 */
export interface TableMeasurementsModel {
  /**
   * The numeric maximum height for the table, ignoring whether the table is
   * maximized or intended to stretch to full height.
   */
  maxTableHeight: number;

  /**
   * The minimum height that the `<table>` tag must be, using the default column
   * header height. Call sites that have measured a taller header row should
   * recompute against that measured height instead.
   */
  minTableHeight: number;
}

/**
 * Creates a memoized selector that computes the TableMeasurementsModel from the store.
 */
export function createTableMeasurementsSelector(
  tableViewsSelector: (store: TableEditableStore) => TableViewsModel
): (store: TableEditableStore) => TableMeasurementsModel {
  return createTableModelSelector(
    [
      (store) => store.stableProps.maximumHeight,
      (store) => store.volatileProps.table.table.length,
      (store) => tableViewsSelector(store).isFullHeight,
    ],
    (maximumHeight, rowCount, isFullHeight) => {
      const maxTableHeight = computeMaxTableHeight(maximumHeight);
      return {
        maxTableHeight,
        minTableHeight: computeMinTableHeight(rowCount, TABLE_AXIS_CELL_HEIGHT, isFullHeight, maxTableHeight),
      };
    }
  );
}
